/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useContext, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import PropTypes from 'prop-types';
import qs from 'qs';
import { useLocation, useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';

import mbpUtil from 'mbp-api-util';

import ReCAPTCHA from 'react-google-recaptcha';
import { form as FormStyle } from '../../style/form';
import { uuidv4 } from '../../../../helpers/uuid';
import { getCryptedOrderDetailsURL } from '../../utils/crypto';
import { deepObjectExtend, isEmpty } from '../../utils/object';
import {
    getFirstItemBillingZipCode,
    getOrderStatus,
    getFirstItemRecipientZipCode,
    getOrderConfirmationId,
} from '../../../../../state/ducks/Checkout/Checkout-Selectors';
import { getSSRDeviceType } from '../../../../../state/ducks/App/App-Selectors';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { schemaWithOptionalCaptcha, schemaWithRequiredCaptcha } from './DesktopTrackOrder/schema/schema';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { OrderDetailsContext } from '../OrderDetails/Context/OrderDetailsContext';
import { getAccessTokenSafely } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { getChannelUser } from '../../state/reducers/Helper';
import getSiteConfig from '../../utils/getSiteConfig';

const styles = ({ typography, palette }) => deepObjectExtend(
    FormStyle(typography, palette),
    {
        form: {
            '& .captchaContainer': {
                width: '100%',
                display: 'block',
                position: 'relative',
                minHeight: '74px',
                marginTop: 24,
                '& #g-recaptcha > div': {
                    width: '100% !important',
                },
            },
            '& .mobileSubmitBtn': {
                fontWeight: 700,
                backgroundColor: palette.colorPrimaryAccountCtaButton,
                border: `1px solid ${palette.colorPrimaryAccountCtaButton}`,
                borderRadius: '20px',
                width: '100%',
                float: 'none',
                padding: '8px 16px',
                textTransform: 'uppercase',
                color: palette.common.white || '#fff',
                '&:hover': {
                    backgroundColor: palette.colorPrimaryAccountCtaButton,
                },
                '&:disabled': {
                    backgroundColor: `${palette.colorDisabled || '#ccc'}`,
                    border: `1px solid ${palette.colorDisabled || '#ccc'}`,
                    color: palette.common.white || '#fff',
                },
            },
        },
    },
);

const TrackOrderByZipCode = ({ mobile, classes }) => {
    const {
        ROSSOrder, createFetchOrderDetailsMutation, errorMessage, setErrorMessage,
    } = useContext(OrderDetailsContext);

    const [callBackURL, setCallBackUrl] = useState('');

    const featureFlags = useSelector((state) => getFeatureFlags(state));

    const APP_GOOGLE_RECAPCHA_KEY = mbpUtil.getEnv('APP_GOOGLE_RECAPCHA_KEY');
    const isOrderTrackingCaptchaOn = featureFlags['is-order-tracking-captcha-on'] || false;
    const isWebUsingNewChannelCode = featureFlags['is-web-using-new-channel-codes'];
    const [showLabel, setShowLabel] = useState({
        orderNumber: !mobile,
        zipCode: !mobile,
        billingZipCode: !mobile,
    });

    const {
        register, handleSubmit, formState: {
            errors, isSubmitted, isValid,
        }, reset, setValue, getValues,
    } = useForm({ mode: 'all', resolver: joiResolver(isOrderTrackingCaptchaOn ? schemaWithRequiredCaptcha : schemaWithOptionalCaptcha) });

    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const orderStatus = useSelector((state) => getOrderStatus(state));
    const orderConfirmationId = useSelector((state) => getOrderConfirmationId(state));
    const getBillingZipCode = useSelector((state) => getFirstItemBillingZipCode(state));
    const zipCode = useSelector((state) => getFirstItemRecipientZipCode(state));
    const ssrDeviceType = useSelector((state) => getSSRDeviceType(state));
    const isReadyState = useSelector((state) => state.account.isReadyState);

    const onRECAPTCHAChange = (captchaToken) => {
        if (!captchaToken) return setValue('recaptcha', '', { shouldValidate: false });
        setValue('recaptcha', captchaToken, { shouldValidate: true });
    };

    const prefillLinkVisitorOrderDetails = () => {
        const preloadedValues = {};
        /* Visitor tracks order as guest through SMS link */
        if (location && location.search) {
            const params = qs.parse(location.search);
            if (params.orderStatusId && params.orderStatusId.match(/^[W]?\d+$/) && params.zipCode) {
                preloadedValues.orderNumber = params.orderStatusId;
                preloadedValues.zipCode = params.zipCode;
                reset(preloadedValues);
            }
        }
        /* Visitor tracks order as guest through Track Order Button on Order Confirmation page */
        /* if zipcode format 12341-1234, chop off -1234 - else leave zipcode as is */
        if (orderStatus === 'M') {
            reset((formValue) => ({
                ...formValue,
                zipCode: /^\d{5}-\d{4}$/.test(zipCode) ? zipCode.slice(0, 5) : zipCode,
                orderNumber: orderConfirmationId,
                billingZipCode: /^\d{5}-\d{4}$/.test(getBillingZipCode) ? getBillingZipCode.slice(0, 5) : getBillingZipCode,
            }));
        }
        return preloadedValues;
    };

    useEffect(() => {
        prefillLinkVisitorOrderDetails();
        sessionStorage.removeItem('isFirstLoad');
    }, []);

    useEffect(() => {
        /*
            It will redirect to path="/account/orders/uuid/order/zip"
            when the Submit button is clicked => `isSubmited` is true
         */
        
        if (isReadyState && isSubmitted && callBackURL && !isEmpty(ROSSOrder) && !errorMessage) {
            history.push(callBackURL);
        }
    }, [isReadyState, ROSSOrder, callBackURL]);

    const onSubmit = async (data) => {
        const { orderNumber, zipCode, billingZipCode } = data;
        const uuid = uuidv4();
        data.uuid = uuid;
        const url = getCryptedOrderDetailsURL(orderNumber, uuid, zipCode, billingZipCode) || '';
        setCallBackUrl(url);
        setErrorMessage('');
        const accessToken = await getAccessTokenSafely();
        const user = getChannelUser(ssrDeviceType, getSiteConfig('domain'), isWebUsingNewChannelCode);
        const rossData = {
            user, orderNumber, recipientZipCode: zipCode, billingZipCode,
        };
        const internalToken = (user.indexOf('30001') > -1) ? mbpUtil.getEnv('ROSS_TOKEN_DESKTOP') : mbpUtil.getEnv('ROSS_TOKEN_MOBILE');
        createFetchOrderDetailsMutation.mutate({ jwt: accessToken, requestBody: rossData, headers: {
            internalToken,
            recaptchaResponse: getValues('recaptcha'),
        }});
        dispatch(trackEvent({
            eventCategory: 'Guest Order Tracking',
            eventAction: 'Click Submit',
            eventLabel: 'Guest order tracking on desktop',
        }));
    };

    return (
        <div className="trackOrderContainer">
            <h2 className="subHeader">Track by Order #</h2>
            <form onSubmit={handleSubmit(onSubmit)} className={`${classes.form} row padding-bottom-2x`}>
                <div className="label-style">
                    {showLabel.orderNumber && <label htmlFor="orderNumber" aria-label="please enter order number">Order #</label>}
                    <input
                        id="orderNumber"
                        type="text"
                        className={`${mobile ? 'input-border-bottom' : 'inputBorder'}  full-width padding-half ${errors?.orderNumber?.message ? 'borderError' : ''}`}
                        placeholder={!showLabel.orderNumber ? 'Order #' : ''}
                        onClick={mobile ? () => setShowLabel({ ...showLabel, orderNumber: true }) : () => {}} // for on click of the input field
                        onBlurCapture={mobile ? () => setShowLabel({ ...showLabel, orderNumber: false }) : () => {}} // using onBlurCapture to not override default onBlur error validation by RHF
                        onFocus={mobile ? () => setShowLabel({ ...showLabel, orderNumber: true }) : () => {}} // for while focus on input field using tab
                        {...register('orderNumber')}
                    />
                    {errors?.orderNumber?.message && <p className="error-color small no-margin-bottom">{errors?.orderNumber?.message}</p>}
                </div>
                <div className="label-style">
                    {showLabel.zipCode && <label htmlFor="zipCode" aria-label="please enter recipient zip code">Recipient Zip Code</label>}
                    <input
                        id="zipCode"
                        type="text"
                        className={`${mobile ? 'input-border-bottom' : 'inputBorder'}  full-width padding-half ${errors?.zipCode?.message ? 'borderError' : ''}`}
                        placeholder={!showLabel.zipCode ? 'Recipient Zip Code' : ''}
                        onClick={mobile ? () => setShowLabel({ ...showLabel, zipCode: true }) : () => {}} // for on click of the input field
                        onBlurCapture={mobile ? () => setShowLabel({ ...showLabel, zipCode: false }) : () => {}} // using onBlurCapture to not override default onBlur error validation by RHF
                        onFocus={mobile ? () => setShowLabel({ ...showLabel, zipCode: true }) : () => {}} // for while focus on input field using tab
                        {...register('zipCode')}
                    />
                    {errors?.zipCode?.message && <p className="error-color small no-margin-bottom">{errors?.zipCode?.message}</p>}
                </div>
                <div className="label-style">
                    {showLabel.billingZipCode && <label htmlFor="billingZipCode" aria-label="please enter billing zip code">Billing Zip Code</label>}
                    <input
                        id="billingZipCode"
                         type="text"
                        className={`${mobile ? 'input-border-bottom' : 'inputBorder'} full-width padding-half ${errors?.billingZipCode?.message ? 'borderError' : ''}`}
                        placeholder={!showLabel.billingZipCode ? 'Billing Zip Code' : ''}
                        onClick={mobile ? () => setShowLabel({ ...showLabel, billingZipCode: true }) : () => {}} // for on click of the input field
                        onBlurCapture={mobile ? () => setShowLabel({ ...showLabel, billingZipCode: false }) : () => {}} // using onBlurCapture to not override default onBlur error validation by RHF
                        onFocus={mobile ? () => setShowLabel({ ...showLabel, billingZipCode: true }) : () => {}} // for while focus on input field using tab
                        {...register('billingZipCode')}
                    />
                    {errors?.billingZipCode?.message && <p className="error-color small no-margin-bottom">{errors?.billingZipCode?.message}</p>}
                </div>
                {errorMessage && <p className="error-color small no-margin-bottom">{!errorMessage.includes('Unable to obtain order details for this order.') ? 'Oops! Something went wrong.' : errorMessage}</p>}
                {isOrderTrackingCaptchaOn && (
                    <div className="captchaContainer">
                        <ReCAPTCHA
                            id="recaptcha"
                            defaultChecked
                            sitekey={APP_GOOGLE_RECAPCHA_KEY}
                            onChange={onRECAPTCHAChange}
                            tabindex="0"
                            size="normal"
                            type="checkbox"
                        />
                        {errors?.recaptcha && (
                            <div className="error-color small no-margin-bottom">{errors.recaptcha.message}</div>
                        )}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={!isReadyState || !isValid}
                    className={`${mobile ? 'mobileSubmitBtn' : 'submitBtn' } margin-top-2x opacity1`}
                >{!isReadyState ? 'Submitting' : 'Submit'}
                </button>
            </form>
        </div>

    );
};

TrackOrderByZipCode.propTypes = {
    isReadyState: PropTypes.bool.isRequired,
};

export default withStyles(styles)(TrackOrderByZipCode);
